import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMapEvents, LayersControl, LayerGroup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css';
import { LocateControl } from 'leaflet.locatecontrol';
import L from 'leaflet';
import { useNavigate, useLocation } from 'react-router-dom';
import markerIcon from '../../assets/icons/RedMap.svg';
import markerIconRetina from '../../assets/icons/RedMap.svg';
import OfficeIcon from '../../assets/icons/OfficeList.svg';
import OtherIcon from '../../assets/icons/OtherList.svg';
import HomeIcon from '../../assets/icons/HomeLIst.svg';
import ShareIcon from '../../assets/icons/share.svg';
import CopyIcon from '../../assets/icons/copy.svg';
import CheckIcon from '../../assets/icons/checked.svg';
import { EmailShareButton, WhatsappShareButton, FacebookShareButton, TwitterShareButton } from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, TwitterIcon } from "react-share";
import FavouriteTrue from '../../assets/icons/favouritetrue.svg';
import FavouriteFalse from '../../assets/icons/favouritefalse.svg';
import HomeAdd from '../../assets/icons/HomeAdd.svg';
import OfficeAdd from '../../assets/icons/OfficeAdd.svg';
import OtherAdd from '../../assets/icons/OtherAdd.svg';
import { Address, getPamCodeSuggessionAsync } from '../../API/Address/GetPamCodeSuggessionAsync';
import { AddtoFavouritesAsync } from '../../API/AddressFavourites/AddToFavouritesAsync';
import { RemoveFromFavouritesAsync } from '../../API/AddressFavourites/RemoveFromFavouritesAsync';
import { useMapSettings } from '../Context/MapSettingsContext';
import { useLocalization } from "../Context/MultiLanguageContext";
import { jwtDecode } from "jwt-decode";
import { toast } from 'react-toastify';
import MarkerTooltip from "./MarkerTooltip";
import ViewModal from './ViewModal';

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIconRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const { BaseLayer } = LayersControl;

L.Marker.prototype.options.icon = defaultIcon;


interface MapComponentProps {
  addresses?: Address[];
  boundaryData?: { latitude: number; longitude: number }[];
  showLocateControl?: boolean;
  children?: React.ReactNode;
  onLocationChange?: (
    latitude: string,
    longitude: string,
    pinCode: string,
    street: string,
    city: string
  ) => void;
}

interface TokenPayload {
  RoleId: number;
}

const LocateControlComponent = () => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const locateOptions = {
      position: 'topright' as L.ControlPosition,
      drawCircle: false,
      flyTo: true,
      setView: true,
      setViewOptions: { maxZoom: 13 },
      locateOptions: {
        enableHighAccuracy: true,
        maxZoom: 13,
      },
    } as any;
    const locateControl = new LocateControl(locateOptions).addTo(map);
    locateControl.start();
    map.on('locationfound', (e: any) => {
      map.setView(e.latlng, 13);
      // L.circle(e.latlng, {
      //   radius: 50,
      //   color: 'blue',
      //   fillColor: '#30f',
      //   fillOpacity: 0.1,
      // }).addTo(map);
    });
    return () => {
      locateControl.stop();
      map.off('locationfound');
      map.removeControl(locateControl);
    };
  }, [map]);
  return null;
};


const FitBoundsHandler: React.FC<{ addresses: Address[] }> = ({ addresses }) => {
  const map = useMap();
  const hasFitted = useRef(false);
  useEffect(() => {
    if (!hasFitted.current && addresses.length > 0) {
      const bounds = addresses.map(addr => [Number(addr.latitude), Number(addr.longitude)] as [number, number]);
      if (bounds.length === 1) {
        map.setView(bounds[0], 17);
      } else {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
      hasFitted.current = true;
    }

    const disableFit = () => { hasFitted.current = true; };
    map.on('zoomstart', disableFit);
    map.on('movestart', disableFit);
    return () => {
      map.off('zoomstart', disableFit);
      map.off('movestart', disableFit);
    };
  }, [addresses, map]);
  return null;
};

const MapComponent: React.FC<MapComponentProps> = ({
  onLocationChange,
  addresses = [],
  boundaryData = [],
  showLocateControl = true,
  children,

}) => {
  const DEFAULT_CENTER: [number, number] = [9.997147, 76.300737];
  const [markerPosition, setMarkerPosition] = useState<[number, number]>(DEFAULT_CENTER);
  const [pincode, setPincode] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [addressResponse, setAddressResponse] = useState<Address[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const [activeSharePamCode, setActiveSharePamCode] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin"; value: string } | null>(null);
  const isFavourite = location.pathname.includes('/favourites');
  const [addressResults, setAddressResults] = useState<Address[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { zoom, layer, setZoom, setLayer } = useMapSettings();
  const initialCenterRef = useRef<[number, number]>(markerPosition);
  const navigate = useNavigate();
  const defaultCenter: [number, number] = initialCenterRef.current;
  const [recenter, setRecenter] = useState<boolean>(false);
  const { t } = useLocalization();
  const [roleId, setRoleId] = useState<number | null>(null);
  const [markerTooltipOpen, setMarkerTooltipOpen] = useState(false);


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode<TokenPayload>(token);
        setRoleId(Number(decodedToken.RoleId));
      } catch (error) {
        // console.error('Error decoding token:', error);
      }
    }
  }, []);

  useEffect(() => {
    setMarkerTooltipOpen(true);
  }, [markerPosition]);


  useEffect(() => {
  }, [roleId]);

  useEffect(() => {
    initialCenterRef.current = markerPosition;
  }, []);
  const MapEvents: React.FC = () => {
    const map = useMapEvents({
      zoomend: () => {
        const currentZoom = map.getZoom();
        setZoom(currentZoom);
        localStorage.setItem("homeMapZoom", currentZoom.toString());
      },
      baselayerchange: (e: any) => {
        setLayer(e.name);
        localStorage.setItem("homeMapLayer", e.name);
      },
    });
    return null;
  }


  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setMarkerPosition([latitude, longitude]);
        localStorage.setItem("selectedHomeMapLocation", JSON.stringify({ lat: latitude, lng: longitude }));
        updateLocationDetails(latitude, longitude);
      },
      (error) => {
        // console.error("Error getting current position", error);
        setMarkerPosition(DEFAULT_CENTER);
        updateLocationDetails(DEFAULT_CENTER[0], DEFAULT_CENTER[1]);
      }
    );
  }, []);

  useEffect(() => {
    if (isFavourite) {
      setMarkerPosition([0, 0]);
    }
  }, [isFavourite]);
  const MapMover: React.FC<{ center: [number, number] }> = ({ center }) => {
    const map = useMap();
    useEffect(() => {
      map.setView(center, map.getZoom());
    }, []);
    return null;
  };



  const updateLocationDetails = (lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);
    localStorage.setItem("selectedHomeMapLocation", JSON.stringify({ lat, lng }));
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then(response => response.json())
      .then(data => {
        const pinCodeValue = data.address?.postcode || 'N/A';
        const streetValue = data.address?.road || 'N/A';
        const cityValue = data.address?.city || data.address?.town || data.address?.village || 'N/A';

        setPincode(pinCodeValue);
        setStreet(streetValue);
        setCity(cityValue);

        if (onLocationChange) {
          onLocationChange(lat.toString(), lng.toString(), pinCodeValue, streetValue, cityValue);
        }
      })
      .catch((error) => console.error('Error fetching location details:', error));
  };



  const goToMapAddAddress = (latitude: number, longitude: number) => {
    if (roleId === 3) {
      toast.info("Please Log In to Add Address", { toastId: "guest-login" });
      return;
    }
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      addressLine1: street || '',
      addressLine2: city || '',
      addressLine3: `${street}, ${city}` || '',
      pinCode: pincode || '',
    });

    navigate(`/add-new-address?${params.toString()}`);
  };


  const fetchAddresses = async () => {
    setIsSubmitting(true);

    const token = localStorage.getItem("token");
    if (!token) {
      // console.error("Token not found in localStorage");
      setIsSubmitting(false);
      return;
    }

    try {
      const data = await getPamCodeSuggessionAsync(
        markerPosition[0],
        markerPosition[1],
        token, t
      );
      if (data === null) {
        setIsSubmitting(false);
        return;
      }
      // console.log("Fetched addresses:", data);
      if (Array.isArray(data) && data.length > 0) {
        // Data is an array of addresses — set it directly.
        setAddressResponse(data as Address[]);
      } else if (data && !Array.isArray(data)) {
        // If API returns a single address object, normalize it into an array.
        setAddressResponse([data as Address]);
      } else {
        console.warn("No data returned from PAM Code suggestions API or data has unexpected shape:", data);
        setAddressResponse([]);
      }

      setIsSubmitting(false);
      setIsModalOpen(true);
    } catch (error) {
      // console.error("Error fetching addresses:", error);
      setAddressResponse([]);
      setIsSubmitting(false);
    }
  };


  const toggleFavourite = (id?: string) => {
    if (!id) return;
    setAddressResponse((prev) =>
      prev.map((address) =>
        address.id === id ? { ...address, isFavourite: !address.isFavourite } : address
      )
    );
  };
  useEffect(() => {
    if (!isModalOpen) {
      setActiveSharePamCode(null);
    }
  }, [isModalOpen]);


  const HeartClick = async (addressId: string, isFavourite: boolean) => {
    const newFavourite = !isFavourite;

    setAddressResults((prevData) =>
      prevData.map((item) =>
        item.id === addressId ? { ...item, isFavourite: newFavourite } : item
      )
    );

    const token = localStorage.getItem('token');
    if (!token) {
      // console.error('Token not found. User might be logged out.');
      return;
    }

    try {
      const numericAddressId = Number(addressId);
      if (isNaN(numericAddressId)) {
        throw new Error("Invalid addressId: Cannot convert to number");
      }

      if (newFavourite) {
        await AddtoFavouritesAsync(numericAddressId, token);
      } else {
        await RemoveFromFavouritesAsync(numericAddressId, token);
      }
    } catch (error) {
      // console.error("An error occurred while updating favourite status:", error);
      revertFavouriteState(addressId, isFavourite);
    }
  };

  const revertFavouriteState = (addressId: string, originalState: boolean) => {
    setAddressResults((prevData) =>
      prevData.map((item) =>
        item.id === addressId ? { ...item, isFavourite: originalState } : item
      )
    );
  };



  const openGoogleMaps = (latitude: number, longitude: number) => {
    const defaultMap = localStorage.getItem("defaultMap") || "google";

    if (defaultMap === "waze") {
      const wazeMapsUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
      window.open(wazeMapsUrl, '_blank');
    } else if (defaultMap === "mapindia") {
      const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
      window.open(mapplsUrl, "_blank");
    } else if (defaultMap === "apple") {
      const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
      window.open(appleMapsUrl, "_blank");
    } else {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(googleMapsUrl, '_blank');
    }
  };
  const MapClickHandler = () => {
    const location = useLocation();
    const isFavourite = location.pathname.includes('/favourites');
    useMapEvents({
      click(e) {
        if (!isFavourite) {
          const { lat, lng } = e.latlng;
          updateLocationDetails(lat, lng);

        }
      },
    });
    return null;
  };



  const handleShareClick = (pamCode: string) => {
    if (activeSharePamCode === pamCode) {
      setActiveSharePamCode(null);
    } else {
      setActiveSharePamCode(pamCode);
    }
  };
  const shareMessage = (address: Address) => {
    return `Check out this address:
    ${address?.addressLine1}, ${address?.addressLine2}, ${address?.addressLine3}
    PAM Code: ${formatPamCode(address?.pamCode || "")}
    DIGI PIN: ${address?.digiPin || "N/A"}
    
    More details: https://getmethere.in`;
  };

  function shareBlockCodeMessage(pinCode: string, blockCode: string, digiPin?: string): string {
    return `Block Code: ${pinCode}-${blockCode}
DIGI PIN: ${digiPin || "N/A"}

More details: https://getmethere.in`;
  }

  const formatPamCode = (pamCode: string) => {
    if (!pamCode) return "N/A";
    const pincode = pamCode.slice(0, 6);
    const blockCode = pamCode.slice(6, 10);
    const addressCode = pamCode.slice(10);

    return `${pincode}-${blockCode}-${addressCode}`;
  };


  const handleCopy = async (value: string, type: "pamCode" | "digiPin") => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField({ type, value });
      setTimeout(() => setCopiedField(null), 2000);

    } catch (err) {
      // console.error('Failed to copy: ', err);
    }
  };


  const getAddressTypeIconHtml = (addressType: string) => {
    const type = addressType.toLowerCase();
    if (type === 'home') {
      return `<img src="${HomeAdd}" alt="Home" style="width:40px; height:40px;" />`;
    } else if (type === 'office') {
      return `<img src="${OfficeAdd}" alt="Office" style="width:40px; height:40px;" />`;
    } else {
      return `<img src="${OtherAdd}" alt="Other" style="width:40px; height:40px;" />`;
    }
  };
  const getMapComponent = () => {
    if (addresses.length > 0) {
      const firstAddress = addresses[0];

      if (firstAddress.pinCode && firstAddress.pinCode.length === 6) {
        return (
          <MapContainer
            center={defaultCenter}
            zoom={zoom}
            className="h-full w-full"
            scrollWheelZoom={true}
            zoomControl={true}
            dragging={true}
            doubleClickZoom={true}
            touchZoom={true}
          >
            <FitBoundsHandler addresses={addresses} />
            <LayersControl position="topright">
              <LayersControl.BaseLayer name={t("defaultView")} checked={layer !== "Satellite View"}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name={t("satelliteView")} checked={layer === "Satellite View"}>
                <LayerGroup>
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="&copy; Esri"
                  />
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    attribution="&copy; Esri"
                    opacity={0.8}
                  />
                </LayerGroup>
              </LayersControl.BaseLayer>
            </LayersControl>

            {addresses.map((address) => {
              const customIcon = L.divIcon({
                html: getAddressTypeIconHtml(address.addressType || 'other'),
                className: '',
                iconSize: [30, 30],
              });
              return (
                <Marker
                  key={address.id || `${address.latitude}-${address.longitude}`}
                  position={[address.latitude, address.longitude]}
                  icon={customIcon}
                  eventHandlers={{
                    click: () => openGoogleMaps(address.latitude, address.longitude),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                    <div className="text-xs">
                      <strong>{address.addressType || "Favourite"}</strong>
                      <br />
                      {address.addressLine1}, {address.addressLine2}, {address.addressLine3}
                      <br />
                      PIN: {address.pinCode}
                    </div>
                  </Tooltip>

                  <Popup>
                    <div className="text-sm">
                      <strong>{address.addressType || "Favourite"}</strong>
                      <br />
                      {address.addressLine1}, {address.addressLine2}, {address.addressLine3}
                      <br />
                      <strong>PIN:</strong> {address.pinCode}
                      <br />
                      {address.pamCode && (
                        <>
                          <strong>PAM:</strong> {formatPamCode(address.pamCode)}
                          <br />
                        </>
                      )}
                      {address.digiPin && (
                        <>
                          <strong>DigiPin:</strong> {address.digiPin}
                        </>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            <MapEvents />

            {showLocateControl && <LocateControlComponent />}
            {children}
          </MapContainer>
        );
      } else if (firstAddress.pinCode && firstAddress.pinCode.length === 10) {
        return (
          <MapContainer center={defaultCenter} zoom={zoom} className="h-full w-full">
            <LayersControl position="topright">
              <LayersControl.BaseLayer name={t("defaultView")} checked={layer !== "Satellite View"}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name={t("satelliteView")} checked={layer === "Satellite View"}>
                <LayerGroup>
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="&copy; Esri"
                  />
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    attribution="&copy; Esri"
                    opacity={0.8}
                  />
                </LayerGroup>
              </LayersControl.BaseLayer>
            </LayersControl>


            {boundaryData.map((boundary, index) => (
              <Marker
                key={index}
                position={[boundary.latitude, boundary.longitude]}
                eventHandlers={{
                  click: () => goToMapAddAddress(boundary.latitude, boundary.longitude),
                }}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent>
                  <span>
                    {t("boundary")}
                  </span>
                </Tooltip>

                {/* <Popup>
                  <div>
                    <strong>{t("boundaryLat")} : </strong> {boundary.latitude}<br />
                    <strong>{t("boundaryLong")} : </strong> {boundary.longitude}<br />
                  </div>
                </Popup> */}
              </Marker>
            ))}
            <MapEvents />

            {showLocateControl && <LocateControlComponent />}
          </MapContainer>
        );
      }
    }

    return (
      <MapContainer
        center={defaultCenter}
        zoom={zoom}
        className="h-full"
        scrollWheelZoom={true}
        zoomControl={true}
        dragging={true}
        doubleClickZoom={true}
        touchZoom={true}
      >
        <FitBoundsHandler addresses={addresses} />
        <LayersControl position="topright">
          <LayersControl.BaseLayer name={t("defaultView")} checked={layer === "Default Map"}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxNativeZoom={19}
              maxZoom={22}
              attribution='&copy; OpenStreetMap contributors'
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name={t("satelliteView")} checked={layer === "Satellite View"}>
            <LayerGroup>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxNativeZoom={19}
                maxZoom={22}
                attribution="&copy; Esri"
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; Esri"
                opacity={0.8}
              />
            </LayerGroup>
          </LayersControl.BaseLayer>
        </LayersControl>

        {addresses.map((address) => (
          <Marker
            key={address.id || `${address.latitude}-${address.longitude}`}
            position={[address.latitude, address.longitude]}
            eventHandlers={{
              click: () => {
                window.open(`https://www.google.com/maps?q=${address.latitude},${address.longitude}`, "_blank");
              },
            }}
          >
            {address.addressLine3 && (
              <>
                <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent>
                  <span>{address.addressLine3}</span>
                </Tooltip>
                <Popup>
                  <div>
                    <strong>{t("address")}:</strong> {address.addressLine1}, {address.addressLine2}<br />
                    <strong>{t("city")}:{address.addressLine3}</strong><br />
                    <strong>{t("postalCode")}:</strong> {address.pinCode}<br />
                  </div>
                </Popup>
              </>
            )}
          </Marker>
        ))}
        {showLocateControl && <LocateControlComponent />}
        <MapClickHandler />
        <Marker
          position={markerPosition}
          eventHandlers={{
            click: () => {
              setMarkerTooltipOpen(true);
            },
          }}
        >
          {((street || city || pincode) && !isFavourite) && markerTooltipOpen && (
            <MarkerTooltip
              street={street}
              city={city}
              pincode={pincode}
              markerPosition={markerPosition}
              isSubmitting={isSubmitting}
              t={t}
              openGoogleMaps={openGoogleMaps}
              goToMapAddAddress={goToMapAddAddress}
              fetchAddresses={fetchAddresses}
              onClose={() => setMarkerTooltipOpen(false)}
            />
          )}
        </Marker>
        <MapEvents />

      </MapContainer>
    );
  };

  return (
    <div className="h-full relative flex">
      <div className="flex-1 relative z-0">
        {getMapComponent()}
      </div>
      <ViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        addressResponse={addressResponse}
        t={t}
        copiedField={copiedField}
        handleCopy={handleCopy}
        activeSharePamCode={activeSharePamCode}
        setActiveSharePamCode={setActiveSharePamCode}
        toggleFavourite={toggleFavourite}
        formatPamCode={formatPamCode}
        shareMessage={shareMessage}
        shareBlockCodeMessage={shareBlockCodeMessage}
      />
    </div>
  );
};

export default MapComponent;